import React, { useState, useEffect, useCallback, useContext } from "react";
import {
  Card,
  Button,
  Tag,
  Descriptions,
  notification,
  Popconfirm,
  Divider,
  Spin,
  Alert,
  Tooltip,
  Tabs,
  Table,
  Icon,
} from "antd";
import Breadcrumbs from "../../components/Breadcrumbs";
import { FreireContext } from "../../utils/freireContext";
import { Show } from "../../contexts/PermissionContext";
import api from "../../services/api";
import * as texts from "./locales";
import "./index.css";
import moment from "moment";

type Status = "UP" | "DOWN" | null;

interface Override {
  set_by: string;
  set_at: string;
}

interface AutoOverrideData {
  status: string;
  reason: string;
  set_at: string;
  current_paid_users: number;
  total_pix_users: number;
  paid_ratio: number;
}

interface LastCheck {
  status: Status;
  latency_ms?: number;
  error_message?: string;
  checked_at: string;
}

interface VolumeMetrics {
  currentPaidUsers: number;
  totalPixUsers: number;
  paidRatio: number;
  minUsersThreshold: number;
  paidRatioThreshold: number;
}

interface HealthCheckStatus {
  status: Status;
  actualStatus: Status;
  hasOverride: boolean;
  hasAutoOverride: boolean;
  override: Override | null;
  autoOverride: AutoOverrideData | null;
  volumeMetrics: VolumeMetrics | null;
  lastCheck: LastCheck | null;
  enabled: boolean;
  message: string | null;
}

interface HealthCheckListItem {
  _id: string;
  status: Status;
  latency_ms?: number;
  error_message?: string;
  checked_at: string;
}

interface VolumeWindow {
  window: string;
  avgUsers: number;
  dataPoints: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
}

const PixHealthCheck: React.FC = () => {
  const { freire } = useContext(FreireContext);

  const [loading, setLoading] = useState<boolean>(false);
  const [status, setStatus] = useState<Status>(null);
  const [actualStatus, setActualStatus] = useState<Status>(null);
  const [hasOverride, setHasOverride] = useState<boolean>(false);
  const [hasAutoOverride, setHasAutoOverride] = useState<boolean>(false);
  const [override, setOverride] = useState<Override | null>(null);
  const [autoOverride, setAutoOverride] = useState<AutoOverrideData | null>(
    null
  );
  const [lastCheck, setLastCheck] = useState<LastCheck | null>(null);
  const [volumeMetrics, setVolumeMetrics] = useState<VolumeMetrics | null>(
    null
  );
  const [enabled, setEnabled] = useState<boolean>(true);
  const [message, setMessage] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState<boolean>(false);
  const [listData, setListData] = useState<HealthCheckListItem[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 25,
    total: 0,
  });
  const [btnActionLoading, setBtnActionLoading] = useState<boolean>(false);
  const [volumeLoading, setVolumeLoading] = useState<boolean>(false);
  const [volumeWindows, setVolumeWindows] = useState<VolumeWindow[]>([]);

  const fetchStatus = useCallback(async (): Promise<void> => {
    setLoading(true);

    try {
      const { data } = await api.get<HealthCheckStatus>(
        "/admin/pix-health-check/status"
      );

      setStatus(data.status);
      setActualStatus(data.actualStatus);
      setHasOverride(data.hasOverride || false);
      setHasAutoOverride(data.hasAutoOverride || false);
      setOverride(data.override);
      setAutoOverride(data.autoOverride || null);
      setVolumeMetrics(data.volumeMetrics || null);
      setLastCheck(data.lastCheck);
      setEnabled(data.enabled ?? true);
      setMessage(data.message);
      setLoading(false);
    } catch (error: any) {
      notification.error({
        message: freire(texts.OOPS),
        description:
          error.response?.data?.error || freire(texts.ERROR_FETCHING_STATUS),
      });
      setLoading(false);
    }
  }, [freire]);

  const setOverrideStatus = useCallback(
    async (status: "UP" | "DOWN"): Promise<void> => {
      setBtnActionLoading(true);

      try {
        await api.put("/admin/pix-health-check/override", {
          status,
        });

        notification.success({
          message: freire(texts.SUCCESS),
          description: freire(texts.OVERRIDE_SET_SUCCESS),
        });

        fetchStatus();
      } catch (error: any) {
        notification.error({
          message: freire(texts.OOPS),
          description:
            error.response?.data?.error || freire(texts.ERROR_SETTING_OVERRIDE),
        });
      } finally {
        setBtnActionLoading(false);
      }
    },
    [freire, fetchStatus]
  );

  const clearOverride = useCallback(async (): Promise<void> => {
    setBtnActionLoading(true);

    try {
      await api.delete("/admin/pix-health-check/override");

      notification.success({
        message: freire(texts.SUCCESS),
        description: freire(texts.OVERRIDE_CLEARED_SUCCESS),
      });

      fetchStatus();
    } catch (error: any) {
      notification.error({
        message: freire(texts.OOPS),
        description:
          error.response?.data?.error || freire(texts.ERROR_CLEARING_OVERRIDE),
      });
    } finally {
      setBtnActionLoading(false);
    }
  }, [freire, fetchStatus]);

  const clearAutoOverrideFn = useCallback(async (): Promise<void> => {
    setBtnActionLoading(true);

    try {
      await api.delete("/admin/pix-health-check/auto-override");

      notification.success({
        message: freire(texts.SUCCESS),
        description: freire(texts.AUTO_OVERRIDE_CLEARED_SUCCESS),
      });

      fetchStatus();
    } catch (error: any) {
      notification.error({
        message: freire(texts.OOPS),
        description:
          error.response?.data?.error ||
          freire(texts.ERROR_CLEARING_AUTO_OVERRIDE),
      });
    } finally {
      setBtnActionLoading(false);
    }
  }, [freire, fetchStatus]);

  const toggleFeature = useCallback(
    async (enable: boolean): Promise<void> => {
      setBtnActionLoading(true);

      try {
        const endpoint = enable
          ? "/admin/pix-health-check/enable"
          : "/admin/pix-health-check/disable";
        await api.put(endpoint);

        notification.success({
          message: freire(texts.SUCCESS),
          description: enable
            ? freire(texts.FEATURE_ENABLED_SUCCESS)
            : freire(texts.FEATURE_DISABLED_SUCCESS),
        });

        fetchStatus();
      } catch (error: any) {
        notification.error({
          message: freire(texts.OOPS),
          description:
            error.response?.data?.error || freire(texts.ERROR_TOGGLING_FEATURE),
        });
      } finally {
        setBtnActionLoading(false);
      }
    },
    [freire, fetchStatus]
  );

  const fetchVolumeStats = useCallback(async (): Promise<void> => {
    setVolumeLoading(true);

    try {
      const { data } = await api.get<{
        windows: VolumeWindow[];
        autoOverride: AutoOverrideData | null;
      }>("/admin/pix-health-check/volume-stats");

      setVolumeWindows(data.windows || []);
      if (data.autoOverride) {
        setAutoOverride(data.autoOverride);
        setHasAutoOverride(true);
      }
    } catch (error: any) {
      notification.error({
        message: freire(texts.OOPS),
        description:
          error.response?.data?.error ||
          freire(texts.ERROR_FETCHING_VOLUME_STATS),
      });
    } finally {
      setVolumeLoading(false);
    }
  }, [freire]);

  const getStatusTag = useCallback(
    (status: Status, showInfo = false): JSX.Element => {
      const cursor = showInfo ? "pointer" : "default";

      const configByStatus: Record<
        Exclude<Status, null>,
        { color: string; text: string }
      > = {
        UP: { color: "green", text: freire(texts.STATUS_ACTIVE) },
        DOWN: { color: "red", text: freire(texts.STATUS_INACTIVE) },
      };

      const config = status ? configByStatus[status] : null;

      return (
        <Tag color={config?.color} style={{ cursor }}>
          {config?.text ?? freire(texts.STATUS_NA)}{" "}
          {showInfo && <Icon type="info-circle" />}
        </Tag>
      );
    },
    [freire]
  );

  const formatDate = useCallback((dateString: string | null | undefined) => {
    if (!dateString) return "-";
    return moment(dateString).format("L LTS");
  }, []);

  const fetchList = useCallback(
    async (page: number = 1, limit: number = 25): Promise<void> => {
      setListLoading(true);

      try {
        const { data } = await api.get<{
          docs: HealthCheckListItem[];
          page: number;
          limit: number;
          totalDocs: number;
        }>("/admin/pix-health-check/list", {
          params: {
            page,
            limit,
          },
        });

        setListData(data.docs || []);
        setPagination({
          page: data.page || page,
          limit: data.limit || limit,
          total: data.totalDocs || 0,
        });
        setListLoading(false);
      } catch (error: any) {
        notification.error({
          message: freire(texts.OOPS),
          description:
            error.response?.data?.error || freire(texts.ERROR_FETCHING_LIST),
        });
        setListLoading(false);
      }
    },
    [freire]
  );

  const handleTableChange = useCallback(
    (pagination: any, filters?: any, sorter?: any): void => {
      if (pagination.current && pagination.pageSize) {
        fetchList(pagination.current, pagination.pageSize);
      }
    },
    [fetchList]
  );

  const handleTabChange = useCallback(
    (activeKey: string): void => {
      if (activeKey === "list" && listData.length === 0) {
        fetchList();
      }
      if (activeKey === "volume") {
        fetchVolumeStats();
      }
    },
    [listData.length, fetchList, fetchVolumeStats]
  );

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(() => {
      fetchStatus();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [fetchStatus]);

  // Current UTC window label (HH:00 or HH:30)
  const currentWindowLabel = (() => {
    const now = new Date();
    const hour = String(now.getUTCHours()).padStart(2, "0");
    const half = now.getUTCMinutes() < 30 ? "00" : "30";
    return `${hour}:${half}`;
  })();

  const columns = [
    {
      title: freire(texts.STATUS),
      dataIndex: "status",
      key: "status",
      render: (status: Status) => getStatusTag(status),
    },
    {
      title: freire(texts.LATENCY),
      dataIndex: "latency_ms",
      key: "latency_ms",
      render: (latency: number | undefined) =>
        latency ? `${latency} ms` : "-",
    },
    {
      title: freire(texts.ERROR_MESSAGE),
      dataIndex: "error_message",
      key: "error_message",
      render: (error: string | undefined) => error || "-",
    },
    {
      title: freire(texts.CHECKED_AT),
      dataIndex: "checked_at",
      key: "checked_at",
      render: (date: string) => formatDate(date),
    },
  ];

  const volumeColumns = [
    {
      title: freire(texts.VOLUME_WINDOW),
      dataIndex: "window",
      key: "window",
      render: (w: string) => (
        <span>
          {w}
          {w === currentWindowLabel && (
            <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
              {freire(texts.VOLUME_CURRENT_WINDOW)}
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: freire(texts.VOLUME_AVG_USERS),
      dataIndex: "avgUsers",
      key: "avgUsers",
      render: (v: number) => v.toFixed(1),
    },
    {
      title: freire(texts.VOLUME_DATA_POINTS),
      dataIndex: "dataPoints",
      key: "dataPoints",
    },
  ];

  return (
    <div className="container">
      <Breadcrumbs
        breadcrumbs={[
          freire(texts.ADMIN),
          freire(texts.MANAGEMENT),
          freire(texts.PIX_HEALTH_CHECK),
        ]}
      />

      <Card title={freire(texts.PIX_HEALTH_CHECK_TITLE)}>
        <Tabs defaultActiveKey="now" onChange={handleTabChange}>
          <Tabs.TabPane tab={freire(texts.TAB_NOW)} key="now">
            <Spin spinning={loading}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 16,
                }}
              >
                <Button icon="reload" onClick={fetchStatus} loading={loading}>
                  {freire(texts.REFRESH)}
                </Button>
              </div>
              {message && (
                <Alert
                  message={message}
                  type="info"
                  showIcon
                  style={{ marginBottom: 24 }}
                />
              )}

              <Descriptions
                title={freire(texts.STATUS_INFORMATION)}
                bordered
                column={1}
              >
                <Descriptions.Item label={freire(texts.FEATURE_STATUS)}>
                  {enabled ? (
                    <Tag color="green">{freire(texts.ENABLED)}</Tag>
                  ) : (
                    <Tag color="red">{freire(texts.DISABLED)}</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label={freire(texts.CURRENT_STATUS)}>
                  {getStatusTag(status)}
                  {hasOverride && override && (
                    <Tooltip
                      title={
                        <div>
                          <div>
                            <strong>{freire(texts.OVERRIDE_SET_BY)}:</strong>{" "}
                            {override.set_by}
                          </div>
                          <div>
                            <strong>{freire(texts.OVERRIDE_SET_AT)}:</strong>{" "}
                            {formatDate(override.set_at)}
                          </div>
                        </div>
                      }
                    >
                      <Tag
                        color="orange"
                        style={{ marginLeft: 8, cursor: "pointer" }}
                      >
                        {freire(texts.OVERRIDE_ACTIVE)}{" "}
                        <Icon type="info-circle" />
                      </Tag>
                    </Tooltip>
                  )}
                </Descriptions.Item>

                <Descriptions.Item label={freire(texts.ACTUAL_STATUS)}>
                  {lastCheck ? (
                    <Tooltip
                      title={
                        <div>
                          {actualStatus === "DOWN" &&
                            lastCheck.error_message && (
                              <div>
                                <strong>
                                  {freire(texts.LAST_CHECK_ERROR)}:
                                </strong>{" "}
                                {lastCheck.error_message}
                              </div>
                            )}
                          <div>
                            <strong>{freire(texts.LAST_CHECK_LATENCY)}:</strong>{" "}
                            {lastCheck.latency_ms
                              ? `${lastCheck.latency_ms} ms`
                              : "-"}
                          </div>
                          <div>
                            <strong>{freire(texts.LAST_CHECK_TIME)}:</strong>{" "}
                            {formatDate(lastCheck.checked_at)}
                          </div>
                        </div>
                      }
                    >
                      {getStatusTag(actualStatus, true)}
                    </Tooltip>
                  ) : (
                    getStatusTag(actualStatus)
                  )}
                </Descriptions.Item>

                <Descriptions.Item label={freire(texts.AUTO_OVERRIDE)}>
                  {hasAutoOverride && autoOverride ? (
                    <Tooltip
                      title={
                        <div>
                          <div>
                            <strong>{freire(texts.AUTO_OVERRIDE_REASON)}:</strong>{" "}
                            {autoOverride.reason}
                          </div>
                          <div>
                            <strong>{freire(texts.AUTO_OVERRIDE_PAID_RATIO)}:</strong>{" "}
                            {(autoOverride.paid_ratio * 100).toFixed(1)}%
                            ({autoOverride.current_paid_users}/{autoOverride.total_pix_users})
                          </div>
                          <div>
                            <strong>{freire(texts.AUTO_OVERRIDE_SET_AT)}:</strong>{" "}
                            {formatDate(autoOverride.set_at)}
                          </div>
                        </div>
                      }
                    >
                      <Tag color="red" style={{ cursor: "pointer" }}>
                        {freire(texts.AUTO_OVERRIDE_ACTIVE_LABEL)}{" "}
                        <Icon type="info-circle" />
                      </Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="green">{freire(texts.AUTO_OVERRIDE_NONE)}</Tag>
                  )}
                </Descriptions.Item>

                {volumeMetrics && (
                  <>
                    <Descriptions.Item label={freire(texts.VOLUME_CURRENT_PAID_RATIO)}>
                      <Tag
                        color={(() => {
                          if (volumeMetrics.totalPixUsers <= volumeMetrics.minUsersThreshold) {
                            return "default";
                          }
                          return volumeMetrics.paidRatio < volumeMetrics.paidRatioThreshold
                            ? "red"
                            : "green";
                        })()}
                      >
                        {(volumeMetrics.paidRatio * 100).toFixed(1)}%
                      </Tag>
                      <span style={{ marginLeft: 8, color: "rgba(0,0,0,0.45)" }}>
                        {volumeMetrics.currentPaidUsers}/{volumeMetrics.totalPixUsers}{" "}
                        {freire(texts.AUTO_OVERRIDE_PAID_USERS_LABEL)}
                        {volumeMetrics.totalPixUsers <= volumeMetrics.minUsersThreshold && (
                          <Tag color="default" style={{ marginLeft: 8, fontSize: 11 }}>
                            {freire(texts.VOLUME_LOW_VOLUME)}
                          </Tag>
                        )}
                      </span>
                    </Descriptions.Item>
                    <Descriptions.Item label={freire(texts.VOLUME_THRESHOLDS)}>
                      <span style={{ color: "rgba(0,0,0,0.65)" }}>
                        {freire(texts.VOLUME_MIN_USERS)}: <strong>{volumeMetrics.minUsersThreshold}</strong>
                        <Divider type="vertical" />
                        {freire(texts.VOLUME_MIN_PAID_RATIO)}: <strong>{(volumeMetrics.paidRatioThreshold * 100).toFixed(0)}%</strong>
                      </span>
                    </Descriptions.Item>
                  </>
                )}
              </Descriptions>

              <Divider />

              <div className="pix-health-check-actions">
                <Card
                  title={freire(texts.OVERRIDE_CONTROLS)}
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                    }}
                  >
                    <div>
                      <p>{freire(texts.OVERRIDE_DESCRIPTION)}</p>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Popconfirm
                        title={freire(texts.CONFIRM_SET_UP)}
                        onConfirm={() => setOverrideStatus("UP")}
                        okText={freire(texts.YES)}
                        cancelText={freire(texts.NO)}
                        disabled={btnActionLoading}
                      >
                        <Button
                          type="primary"
                          icon="check-circle"
                          loading={btnActionLoading}
                          disabled={btnActionLoading}
                        >
                          {freire(texts.SET_UP)}
                        </Button>
                      </Popconfirm>
                      <Popconfirm
                        title={freire(texts.CONFIRM_SET_DOWN)}
                        onConfirm={() => setOverrideStatus("DOWN")}
                        okText={freire(texts.YES)}
                        cancelText={freire(texts.NO)}
                        disabled={btnActionLoading}
                      >
                        <Button
                          type="danger"
                          icon="close-circle"
                          loading={btnActionLoading}
                          disabled={btnActionLoading}
                        >
                          {freire(texts.SET_DOWN)}
                        </Button>
                      </Popconfirm>
                      {hasOverride && (
                        <Popconfirm
                          title={freire(texts.CONFIRM_CLEAR_OVERRIDE)}
                          onConfirm={clearOverride}
                          okText={freire(texts.YES)}
                          cancelText={freire(texts.NO)}
                          disabled={btnActionLoading}
                        >
                          <Button
                            icon="delete"
                            loading={btnActionLoading}
                            disabled={btnActionLoading}
                          >
                            {freire(texts.CLEAR_OVERRIDE)}
                          </Button>
                        </Popconfirm>
                      )}
                    </div>
                  </div>
                </Card>

                <Show when="pix-health-check-toggle-feature">
                  <Card title={freire(texts.FEATURE_CONTROLS)} size="small">
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      <div>
                        <p>{freire(texts.FEATURE_DESCRIPTION)}</p>
                        <p>{freire(texts.FEATURE_IMPORTANT)}</p>
                      </div>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {enabled ? (
                          <Popconfirm
                            title={freire(texts.CONFIRM_DISABLE_FEATURE)}
                            onConfirm={() => toggleFeature(false)}
                            okText={freire(texts.YES)}
                            cancelText={freire(texts.NO)}
                            disabled={btnActionLoading}
                          >
                            <Button
                              type="default"
                              icon="close-circle"
                              loading={btnActionLoading}
                              disabled={btnActionLoading}
                            >
                              {freire(texts.DISABLE_FEATURE)}
                            </Button>
                          </Popconfirm>
                        ) : (
                          <Popconfirm
                            title={freire(texts.CONFIRM_ENABLE_FEATURE)}
                            onConfirm={() => toggleFeature(true)}
                            okText={freire(texts.YES)}
                            cancelText={freire(texts.NO)}
                            disabled={btnActionLoading}
                          >
                            <Button
                              type="primary"
                              icon="check-circle"
                              loading={btnActionLoading}
                              disabled={btnActionLoading}
                            >
                              {freire(texts.ENABLE_FEATURE)}
                            </Button>
                          </Popconfirm>
                        )}
                      </div>
                    </div>
                  </Card>
                </Show>
              </div>
            </Spin>
          </Tabs.TabPane>
          <Tabs.TabPane tab={freire(texts.TAB_LIST)} key="list">
            <Table
              style={{ backgroundColor: "white" }}
              loading={listLoading}
              rowKey="_id"
              dataSource={listData}
              columns={columns}
              pagination={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total,
                showSizeChanger: true,
                pageSizeOptions: ["10", "25", "50", "100"],
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} de ${total} itens`,
              }}
              onChange={handleTableChange}
            />
          </Tabs.TabPane>
          <Tabs.TabPane tab={freire(texts.TAB_VOLUME)} key="volume">
            <Spin spinning={volumeLoading}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginBottom: 16,
                }}
              >
                <Button
                  icon="reload"
                  onClick={fetchVolumeStats}
                  loading={volumeLoading}
                >
                  {freire(texts.REFRESH)}
                </Button>
              </div>

              {hasAutoOverride && autoOverride && (
                <Alert
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={freire(texts.AUTO_OVERRIDE_ALERT_TITLE)}
                  description={
                    <div>
                      <div>
                        <strong>{freire(texts.AUTO_OVERRIDE_REASON)}:</strong>{" "}
                        {autoOverride.reason}
                      </div>
                      <div>
                        <strong>{freire(texts.AUTO_OVERRIDE_PAID_RATIO)}:</strong>{" "}
                        {(autoOverride.paid_ratio * 100).toFixed(1)}%
                        ({autoOverride.current_paid_users}/{autoOverride.total_pix_users}{" "}
                        {freire(texts.AUTO_OVERRIDE_PAID_USERS_LABEL)})
                      </div>
                      <div>
                        <strong>{freire(texts.AUTO_OVERRIDE_SET_AT)}:</strong>{" "}
                        {formatDate(autoOverride.set_at)}
                      </div>
                      <div style={{ marginTop: 8 }}>
                        <Popconfirm
                          title={freire(texts.CONFIRM_CLEAR_AUTO_OVERRIDE)}
                          onConfirm={clearAutoOverrideFn}
                          okText={freire(texts.YES)}
                          cancelText={freire(texts.NO)}
                          disabled={btnActionLoading}
                        >
                          <Button
                            type="danger"
                            icon="delete"
                            size="small"
                            loading={btnActionLoading}
                            disabled={btnActionLoading}
                          >
                            {freire(texts.CLEAR_AUTO_OVERRIDE)}
                          </Button>
                        </Popconfirm>
                      </div>
                    </div>
                  }
                />
              )}

              <Table
                style={{ backgroundColor: "white" }}
                rowKey="window"
                dataSource={volumeWindows}
                columns={volumeColumns}
                pagination={false}
                size="small"
                rowClassName={(record) =>
                  record.window === currentWindowLabel
                    ? "pix-volume-current-row"
                    : ""
                }
              />
            </Spin>
          </Tabs.TabPane>
        </Tabs>
      </Card>
    </div>
  );
};

export default PixHealthCheck;
